import { defineStore } from 'pinia'

const enabledStorageKey = 'chat.browser_notifications.enabled'

const browserNotificationsSupported = () => {
  return typeof window !== 'undefined' && 'Notification' in window
}

const currentPermission = () => {
  if (!browserNotificationsSupported()) {
    return 'unsupported'
  }

  return window.Notification.permission
}

const notificationUrl = (message) => {
  return `/chat/${message.conversation_id}`
}

const notificationTitle = (message, conversation = null) => {
  const senderName = message.sender?.name

  if (typeof senderName === 'string' && senderName.trim() !== '') {
    return `New message from ${senderName.trim()}`
  }

  const conversationTitle = conversation?.title

  if (typeof conversationTitle === 'string' && conversationTitle.trim() !== '') {
    return conversationTitle.trim()
  }

  return 'New chat message'
}

const notificationBody = (message) => {
  if (typeof message.body === 'string' && message.body.trim() !== '') {
    return message.body.trim()
  }

  const uploads = Array.isArray(message.uploads) ? message.uploads : []

  if (uploads.length > 1) {
    return `Sent ${uploads.length} files.`
  }

  if (uploads.length === 1) {
    const upload = uploads[0]

    if (String(upload?.mime_type ?? '').toLowerCase().startsWith('image/')) {
      return 'Sent an image.'
    }

    return upload?.filename ? `Sent ${upload.filename}.` : 'Sent a file.'
  }

  return matchMessageType(message.type)
}

const matchMessageType = (messageType) => {
  if (messageType === 'image') {
    return 'Sent an image.'
  }

  if (messageType === 'file') {
    return 'Sent a file.'
  }

  if (messageType === 'mixed') {
    return 'Sent a message with attachments.'
  }

  return 'Sent a message.'
}

export const useBrowserNotificationStore = defineStore('chat-browser-notifications', {
  state: () => ({
    isSupported: browserNotificationsSupported(),
    permission: currentPermission(),
    isEnabled: false,
    error: null,
  }),

  getters: {
    canRequestPermission(state) {
      return state.isSupported && state.permission !== 'denied'
    },
    statusLabel(state) {
      if (!state.isSupported) {
        return 'Browser notifications are not supported'
      }

      if (state.permission === 'denied') {
        return 'Browser notifications are blocked'
      }

      return state.isEnabled ? 'Disable browser notifications' : 'Enable browser notifications'
    },
  },

  actions: {
    initialize() {
      this.isSupported = browserNotificationsSupported()
      this.permission = currentPermission()

      if (!this.isSupported || this.permission !== 'granted') {
        this.isEnabled = false
        return
      }

      this.isEnabled = window.localStorage?.getItem(enabledStorageKey) !== 'false'
    },

    async requestPermission() {
      this.error = null
      this.initialize()

      if (!this.isSupported) {
        this.error = 'Browser notifications are not supported.'
        return this.permission
      }

      if (this.permission !== 'granted') {
        this.permission = await window.Notification.requestPermission()
      }

      this.isEnabled = this.permission === 'granted'
      window.localStorage?.setItem(enabledStorageKey, this.isEnabled ? 'true' : 'false')

      return this.permission
    },

    disable() {
      this.isEnabled = false
      window.localStorage?.setItem(enabledStorageKey, 'false')
    },

    async toggle() {
      if (this.isEnabled) {
        this.disable()
        return this.permission
      }

      return this.requestPermission()
    },

    notifyMessage(message, conversation = null) {
      this.initialize()

      if (!this.isEnabled || this.permission !== 'granted' || !message?.conversation_id) {
        return null
      }

      const notification = new window.Notification(notificationTitle(message, conversation), {
        body: notificationBody(message),
        tag: `chat-message-${message.id}`,
        data: {
          url: notificationUrl(message),
          conversation_id: message.conversation_id,
          message_id: message.id,
        },
      })

      notification.onclick = () => {
        window.focus()
        window.location.assign(notification.data.url)
        notification.close()
      }

      return notification
    },
  },
})
